import { EventTracker, EventType } from './eventTracker';
import { MainFunnel } from './pageTracker';

/**
 * Información de un producto en el carrito
 */
export interface CartProduct {
  productId: string;
  productName: string;
  price?: number;
  quantity: number;
  imageUrl?: string;
  [key: string]: any;
}

/**
 * Información del carrito
 */
export interface CartInfo {
  items: CartProduct[];
  totalItems: number;
  subtotal: number;
  [key: string]: any;
}

/**
 * Clase para trackear eventos del carrito de compras
 */
export class CartTracker {
  private eventTracker: EventTracker;
  public apiUrl: string;
  constructor(apiUrl: string, eventTracker: EventTracker) {
    this.apiUrl = apiUrl;
    this.eventTracker = eventTracker;
  }
  
  /**
   * Trackea un evento de añadir al carrito
   * @param product Información del producto añadido al carrito
   * @param cart Estado actual del carrito después de añadir el producto
   */
  trackAddToCart(product: CartProduct, cart?: CartInfo): void {
    this.eventTracker.track(
      EventType.ADD_TO_CART,
      {
        product,
        cart,
        timestamp: new Date().toISOString()
      },
      'main',
      MainFunnel.CART
    );
  }
  
  /**
   * Trackea un evento de eliminar del carrito
   * @param product Información del producto eliminado del carrito
   * @param cart Estado actual del carrito después de eliminar el producto
   */
  trackRemoveFromCart(product: CartProduct, cart?: CartInfo): void {
    this.eventTracker.track(
      EventType.REMOVE_FROM_CART,
      {
        product,
        cart,
        timestamp: new Date().toISOString()
      }
    );
  }
  
  /**
   * Trackea un evento de actualizar cantidad en el carrito
   * @param product Información del producto actualizado
   * @param oldQuantity Cantidad anterior del producto
   * @param newQuantity Nueva cantidad del producto
   * @param cart Estado actual del carrito después de la actualización
   */
  trackUpdateCart(
    product: CartProduct,
    oldQuantity: number,
    newQuantity: number,
    cart?: CartInfo
  ): void {
    this.eventTracker.track(
      EventType.UPDATE_CART,
      {
        product,
        oldQuantity,
        newQuantity,
        cart,
        timestamp: new Date().toISOString()
      }
    );
  }
  
  /**
   * Trackea un evento de vaciar el carrito
   * @param previousCart Estado anterior del carrito antes de vaciarlo
   */
  trackClearCart(previousCart?: CartInfo): void {
    this.eventTracker.track(
      EventType.CLEAR_CART,
      {
        previousCart,
        timestamp: new Date().toISOString()
      }
    );
  }
  
  /**
   * Trackea un evento de visualización del carrito
   * @param cart Estado actual del carrito
   */
  trackCartView(cart: CartInfo): void {
    this.eventTracker.track(
      EventType.PAGE_VIEW,
      {
        cart,
        pageType: 'cart',
        timestamp: new Date().toISOString()
      },
      'main',
      MainFunnel.CART
    );
  }
  
  /**
   * Trackea un evento de inicio de checkout
   * @param cart Estado actual del carrito al iniciar el checkout
   */
  trackBeginCheckout(cart: CartInfo): void {
    this.eventTracker.track(
      EventType.BEGIN_CHECKOUT,
      {
        cart,
        timestamp: new Date().toISOString()
      },
      'main',
      MainFunnel.CHECKOUT
    );
  }
  
  /**
   * Trackea un evento de compra completada
   * @param orderId ID de la orden
   * @param products Productos comprados
   * @param orderInfo Información adicional de la orden
   */
  trackPurchase(
    orderId: string,
    products: CartProduct[],
    orderInfo: {
      total: number;
      tax?: number;
      shipping?: number;
      discount?: number;
      paymentMethod?: string;
      [key: string]: any;
    }
  ): void {
    this.eventTracker.trackImmediate(
      EventType.PURCHASE,
      {
        orderId,
        products,
        orderInfo,
        timestamp: new Date().toISOString()
      },
      'main',
      MainFunnel.PURCHASE
    );
  }
  
  /**
   * Trackea un evento de añadir a la lista de deseos
   * @param product Información del producto añadido a la lista de deseos
   */
  trackAddToWishlist(product: {
    productId: string;
    productName: string;
    price?: number;
    [key: string]: any;
  }): void {
    this.eventTracker.track(
      EventType.ADD_TO_WISHLIST,
      {
        product,
        timestamp: new Date().toISOString()
      }
    );
  }
  
  /**
   * Trackea un evento de eliminar de la lista de deseos
   * @param product Información del producto eliminado de la lista de deseos
   */
  trackRemoveFromWishlist(product: {
    productId: string;
    productName: string;
    [key: string]: any;
  }): void {
    this.eventTracker.track(
      EventType.REMOVE_FROM_WISHLIST,
      {
        product,
        timestamp: new Date().toISOString()
      }
    );
  }
} 